// GreetingBanner.js
// One-line greeting: "Hi {firstName}" + the date, no gradient, no motivation
// line. See docs/superpowers/specs/2026-09-16-home-redesign.md §5 (Greeting).
import React, { useMemo } from "react";
import { View, Text, StyleSheet } from "react-native";

import { homeColors } from "./homeTheme";

/**
 * Map app language codes to locale strings for date formatting.
 */
const langToLocale = {
  en: "en-US", es: "es-ES", hi: "hi-IN", fr: "fr-FR", zh: "zh-CN",
  ja: "ja-JP", ko: "ko-KR", de: "de-DE", it: "it-IT", ru: "ru-RU",
  ar: "ar-SA", pt: "pt-BR", gu: "gu-IN", te: "te-IN", mr: "mr-IN",
};

/**
 * Format date as "Tuesday, 16 September" in the user's language.
 */
const formatDate = (lang) => {
  const options = { weekday: "long", month: "long", day: "numeric" };
  const locale = langToLocale[lang] || undefined;
  return new Date().toLocaleDateString(locale, options);
};

/** First word of the display name, or the translated "Student" fallback. */
const firstNameOf = (userName, t) => {
  const trimmed = String(userName || "").trim();
  return trimmed ? trimmed.split(/\s+/)[0] : t("Student");
};

/**
 * GreetingBanner - one-line "Hi {firstName} · {date}" greeting.
 * `showMotivation` is kept for existing callers but no longer rendered.
 */
const GreetingBanner = ({
  userName = "Student",
  isDarkMode = false,
  showMotivation = true, // eslint-disable-line no-unused-vars
  style,
  t = (s) => s,
  lang,
}) => {
  const colors = homeColors(isDarkMode);
  const firstName = useMemo(() => firstNameOf(userName, t), [userName, t]);
  const dateStr = useMemo(() => formatDate(lang), [lang]);

  return (
    <View style={[styles.wrap, style]}>
      <Text style={[styles.greeting, { color: colors.textPrimary }]}>
        {t("Hi")} {firstName}
      </Text>
      <Text style={[styles.date, { color: colors.ink3 }]}> · {dateStr}</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    flexDirection: "row",
    alignItems: "baseline",
    flexWrap: "wrap",
  },
  greeting: {
    fontSize: 22,
    fontWeight: "700",
  },
  date: {
    fontSize: 14,
  },
});

export default GreetingBanner;
